import { cartProductSchema } from "@features/product/utils/toCartProduct";
import z from "zod";
import { pushGtmEvent } from "./utils";

export const addToCartEventInputSchema = z.object({
  items: z.array(cartProductSchema),
  currency: z.string(),
});

export type AddToCartEventInput = z.infer<typeof addToCartEventInputSchema>;

export const addToCartEventPayloadSchema = z
  .object({
    event: z.literal("add_to_cart"),
    ecommerce: z.object({
      currency: z.string(),
      value: z.number(),
      items: z.array(
        z.object({
          item_id: z.string(),
          item_name: z.string(),
          item_brand: z.string().optional(),
          item_category: z.string().optional(),
          item_category2: z.string().optional(),
          item_category3: z.string().optional(),
          price: z.number(),
          quantity: z.number(),
          discount: z.number().optional(),
        }),
      ),
    }),
  })
  .optional();

export type AddToCartEventPayload = z.infer<typeof addToCartEventPayloadSchema>;

function buildAddToCartEventPayload(input: AddToCartEventInput) {
  const data = addToCartEventInputSchema.parse(input);

  if (!data) {
    return;
  }

  const value = data.items.reduce(
    (acc, item) => acc + item.price * item.quantity,
    0,
  );

  return addToCartEventPayloadSchema.parse({
    event: "add_to_cart",
    ecommerce: {
      currency: data.currency,
      value,
      items: data.items.map((item) => ({
        item_id: item.partNo,
        item_name: item.name,
        item_brand: item.brand,
        price: item.price,
        quantity: item.quantity,
        discount: item.discount,
      })),
    },
  });
}

export function addToCartEvent(props: AddToCartEventInput) {
  if (typeof window === "undefined") {
    return;
  }

  if (!props.items.length) {
    return;
  }

  try {
    const payload = buildAddToCartEventPayload(props);
    pushGtmEvent(payload);
  } catch {}
}
